import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Target, TrendingUp, BookOpen, Shield, Calendar, ArrowLeft, Leaf, Heart, Building2, BarChart3, Briefcase } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';

export const ESGTraining = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "ESG Training Course",
    "description": "Build ESG capabilities with structured ESG Training Course for corporates. Learn compliance, reporting standards & responsible governance.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": "https://isdeacademy.com"
    },
    "coursePrerequisites": "No prior ESG experience required",
    "educationalLevel": "Beginner to Advanced",
    "audience": {
      "@type": "Audience",
      "audienceType": "Corporate Leaders, Sustainability Managers, Compliance Professionals, Finance Professionals, HR Managers, Consultants"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "Blended",
      "duration": "P2D"
    },
    "teaches": [
      "ESG fundamentals and strategy",
      "ESG frameworks and standards (GRI, SASB, TCFD, BRSR)",
      "BRSR compliance and SEBI reporting for Indian companies",
      "ESG risk assessment",
      "Sustainability reporting",
      "Corporate governance",
      "Environmental management"
    ],
    "occupationalCredentialAwarded": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "Professional Certification",
      "name": "ESG Practitioner Certification"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Who should enroll in an ESG Training Course?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "An ESG Training Course is suitable for Corporate leaders and senior management, Sustainability and ESG managers, Compliance and risk professionals, Finance and investment professionals, HR and operations managers, Supply chain and procurement professionals, Consultants and auditors, and Students interested in sustainability careers."
        }
      },
      {
        "@type": "Question",
        "name": "What are the key pillars covered in ESG training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ESG training covers three pillars: Environmental (climate change, carbon management, energy efficiency, waste management), Social (labor practices, diversity & inclusion, workplace safety, community engagement), and Governance (corporate governance, board responsibilities, ethics, risk management)."
        }
      },
      {
        "@type": "Question",
        "name": "What frameworks are covered in ESG training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ESG training covers major frameworks including GRI (Global Reporting Initiative), SASB (Sustainability Accounting Standards Board), TCFD (Task Force on Climate-related Financial Disclosures), UN SDGs (Sustainable Development Goals), BRSR (Business Responsibility and Sustainability Reporting), and Integrated Reporting."
        }
      },
      {
        "@type": "Question",
        "name": "What is BRSR and is it covered in ESG training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "BRSR (Business Responsibility and Sustainability Reporting) is India's SEBI-mandated ESG disclosure framework applicable to the top 1000 listed companies by market capitalisation. Our ESG training course includes dedicated BRSR modules covering mandatory and leadership indicators, BRSR Core reporting, and alignment with GRI, SASB, and TCFD. It is essential for listed Indian companies to comply with SEBI's BRSR requirements."
        }
      }
    ]
  };

  const esgCovers = [
    "Environmental sustainability and climate action",
    "Social responsibility and ethical business practices",
    "Corporate governance, transparency, and accountability",
    "ESG reporting frameworks and regulations"
  ];

  const importancePoints = [
    {
      title: "Rising Regulatory and Compliance Requirements",
      description: "Governments and regulators across the world are introducing stricter ESG-related regulations and disclosure requirements. ESG training helps organizations stay compliant and prepared for audits and reporting obligations."
    },
    {
      title: "Investor and Stakeholder Expectations",
      description: "Investors increasingly evaluate companies based on ESG performance. Trained professionals help organizations meet investor expectations and improve access to capital."
    },
    {
      title: "Risk Management and Business Resilience",
      description: "ESG risks such as climate change, supply chain disruptions, labor issues, and governance failures can significantly impact business continuity. ESG training enables proactive risk identification and mitigation."
    },
    {
      title: "Competitive Advantage and Brand Reputation",
      description: "Companies with strong ESG practices enjoy enhanced brand trust, customer loyalty, and competitive positioning in the market."
    },
    {
      title: "Long-Term Value Creation",
      description: "ESG integration supports sustainable growth by aligning business goals with environmental stewardship, social impact, and ethical governance."
    }
  ];

  const environmentalTopics = [
    "Climate change and carbon management",
    "Energy efficiency and renewable energy",
    "Waste management and circular economy",
    "Water conservation",
    "Environmental compliance and regulations"
  ];

  const socialTopics = [
    "Labor practices and human rights",
    "Diversity, equity, and inclusion (DEI)",
    "Workplace health and safety",
    "Community engagement and social impact",
    "Customer responsibility and data privacy"
  ];

  const governanceTopics = [
    "Corporate governance structures",
    "Board responsibilities and independence",
    "Ethical conduct and anti-corruption",
    "Risk management and internal controls",
    "Compliance and regulatory oversight"
  ];

  const targetAudience = [
    "Corporate leaders and senior management",
    "Sustainability and ESG managers",
    "Compliance and risk professionals",
    "Finance and investment professionals",
    "HR and operations managers",
    "Supply chain and procurement professionals",
    "Consultants and auditors",
    "Students and graduates interested in sustainability careers"
  ];

  const esgFrameworks = [
    "GRI (Global Reporting Initiative)",
    "SASB (Sustainability Accounting Standards Board)",
    "TCFD (Task Force on Climate-related Financial Disclosures)",
    "UN SDGs (Sustainable Development Goals)",
    "Integrated Reporting (<IR>)"
  ];

  const keyComponents = [
    {
      title: "ESG Fundamentals and Strategy",
      description: "Participants learn the core principles of ESG and how to align them with organizational strategy and business objectives."
    },
    {
      title: "ESG Frameworks and Standards",
      description: "Training covers widely used ESG frameworks and standards including GRI, SASB, TCFD, UN SDGs, and Integrated Reporting."
    },
    {
      title: "ESG Risk Assessment and Materiality",
      description: "Participants learn how to identify ESG risks and opportunities, conduct materiality assessments, and prioritize ESG issues based on stakeholder impact."
    },
    {
      title: "ESG Reporting and Disclosure",
      description: "A critical part focusing on ESG data collection and metrics, sustainability reporting, regulatory disclosures, and transparency and assurance."
    },
    {
      title: "Implementation and Performance Measurement",
      description: "The course emphasizes practical implementation through ESG policy development, KPI setting and monitoring, continuous improvement practices, and ESG integration into operations."
    }
  ];

  const benefits = [
    {
      title: "Professional Skill Enhancement",
      description: "Participants gain in-demand skills in sustainability, governance, and responsible business practices."
    },
    {
      title: "Career Growth and Opportunities",
      description: "ESG professionals are increasingly sought after across industries, offering strong career growth and leadership opportunities."
    },
    {
      title: "Improved Organizational Performance",
      description: "Trained teams help organizations improve sustainability performance and long-term profitability."
    },
    {
      title: "Enhanced Stakeholder Trust",
      description: "Transparent ESG practices build trust with investors, regulators, customers, and employees."
    },
    {
      title: "Future-Ready Organizations",
      description: "ESG training prepares organizations to adapt to future regulatory, environmental, and social challenges."
    }
  ];

  const providerCriteria = [
    "Expertise in ESG and sustainability",
    "Up-to-date curriculum aligned with global standards",
    "Practical case studies and real-world examples",
    "Industry-relevant training",
    "Certification or credential recognition"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="ESG Training Course | BRSR Compliance & ESG Certification | ISDE Academy"
        description="Build ESG capabilities with our comprehensive ESG Training Course. Covers BRSR compliance (SEBI mandate), GRI, SASB, TCFD, UN SDGs. Ideal for Indian corporates & global sustainability leaders."
        keywords="ESG Training Course, ESG certification, BRSR compliance, BRSR training India, SEBI ESG reporting, sustainability training, environmental social governance, ESG compliance, GRI training, SASB training, TCFD, corporate sustainability, responsible governance, ESG risk management, business responsibility sustainability reporting"
        canonical="/esg-training-course"
        structuredData={[structuredData, faqStructuredData]}
        breadcrumbs={[
          { name: 'Training', path: '/training' },
          { name: 'ESG Training', path: '/esg-training-course' }
        ]}
      />

      {/* Hero Section with H1 */}
      <header className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              ESG Training Course
            </h1>
          </motion.div>
        </div>
      </header>

      {/* Back Button */}
      <nav className="bg-gray-50 py-6" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            onClick={() => navigate('/training/iso-training-services')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to ISO Training Services
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-12">
        <article>
          {/* Introduction Section */}
          <section className="mb-16">
            <Card>
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Environmental, Social, and Governance (ESG) factors have become critical drivers of business strategy, investment decisions, and corporate reputation worldwide. Organizations today are expected not only to generate profits but also to operate responsibly, transparently, and sustainably. This growing focus has created strong demand for professionals trained in ESG principles, frameworks, and implementation practices.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  An <strong>ESG Training Course</strong> equips individuals and organizations with the knowledge and practical skills needed to integrate sustainability into business operations, manage ESG risks, comply with regulations, and meet stakeholder expectations. From corporates and financial institutions to manufacturing, healthcare, and technology sectors, ESG training is now a strategic necessity rather than an optional initiative.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  An ESG Training Course is a structured learning program designed to educate professionals on Environmental, Social, and Governance principles and how they apply to business strategy, risk management, compliance, and reporting. The course helps participants understand how ESG factors impact long-term business value, regulatory compliance, and investor confidence.
                </p>
                <p className="text-lg text-gray-700 mb-4">ESG training typically covers:</p>
                <ul className="grid md:grid-cols-2 gap-3 list-none">
                  {esgCovers.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" aria-hidden="true" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Importance Section - H2 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <TrendingUp className="h-8 w-8 text-emerald-600" aria-hidden="true" />
                Importance of an ESG Training Course
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {importancePoints.map((point, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-emerald-100 p-3 rounded-lg flex-shrink-0">
                          <span className="text-emerald-600 font-bold text-lg" aria-hidden="true">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-bold mb-2 text-gray-900">{point.title}</h4>
                          <p className="text-gray-700 text-sm">{point.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Key Pillars - H3 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Target className="h-7 w-7 text-emerald-600" aria-hidden="true" />
                Key Pillars Covered in an ESG Training Course
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Environmental */}
                <Card className="border-t-4 border-t-green-500">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-green-100 p-3 rounded-lg">
                        <Leaf className="h-6 w-6 text-green-600" />
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">1. Environmental (E)</h4>
                    </div>
                    <p className="text-gray-700 mb-4 text-sm">
                      The environmental component focuses on how organizations manage their impact on the natural environment. Topics include:
                    </p>
                    <ul className="space-y-2 list-none">
                      {environmentalTopics.map((topic, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-1" aria-hidden="true" />
                          <span className="text-gray-700 text-sm">{topic}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-gray-600 text-sm mt-4 italic">
                      Participants learn how to measure, reduce, and report environmental impact.
                    </p>
                  </CardContent>
                </Card>

                {/* Social */}
                <Card className="border-t-4 border-t-blue-500">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <Heart className="h-6 w-6 text-blue-600" />
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">2. Social (S)</h4>
                    </div>
                    <p className="text-gray-700 mb-4 text-sm">
                      The social pillar addresses how organizations manage relationships with employees, customers, suppliers, and communities. Key topics include:
                    </p>
                    <ul className="space-y-2 list-none">
                      {socialTopics.map((topic, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-1" aria-hidden="true" />
                          <span className="text-gray-700 text-sm">{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Governance */}
                <Card className="border-t-4 border-t-purple-500">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-purple-100 p-3 rounded-lg">
                        <Building2 className="h-6 w-6 text-purple-600" />
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">3. Governance (G)</h4>
                    </div>
                    <p className="text-gray-700 mb-4 text-sm">
                      Governance focuses on leadership, transparency, and ethical decision-making. Topics include:
                    </p>
                    <ul className="space-y-2 list-none">
                      {governanceTopics.map((topic, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-purple-600 flex-shrink-0 mt-1" aria-hidden="true" />
                          <span className="text-gray-700 text-sm">{topic}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-gray-600 text-sm mt-4 italic">
                      Strong governance ensures accountability and trust.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </section>

          {/* Who Should Enroll - H3 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Users className="h-7 w-7 text-emerald-600" aria-hidden="true" />
                Who Should Enroll in an ESG Training Course?
              </h3>
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">An ESG Training Course is suitable for professionals across industries and roles, including:</p>
                  <ul className="grid md:grid-cols-2 gap-4 list-none">
                    {targetAudience.map((audience, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{audience}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 mt-6 font-semibold">
                    Both beginners and experienced professionals benefit from ESG training.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Key Components - H3 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <BookOpen className="h-7 w-7 text-emerald-600" aria-hidden="true" />
                Key Components of an ESG Training Course
              </h3>
              <div className="space-y-4">
                {keyComponents.map((component, index) => (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-emerald-100 p-3 rounded-lg flex-shrink-0">
                          <span className="text-emerald-600 font-bold text-lg" aria-hidden="true">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold mb-2 text-gray-900">{component.title}</h4>
                          <p className="text-gray-700">{component.description}</p>
                          {index === 1 && (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {esgFrameworks.map((framework, idx) => (
                                <span key={idx} className="px-3 py-1 bg-emerald-50 text-emerald-700 text-sm rounded-full">
                                  {framework}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Benefits - H4 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h4 className="text-2xl md:text-3xl font-bold mb-8 text-gray-900 flex items-center gap-3">
                <BarChart3 className="h-7 w-7 text-emerald-600" aria-hidden="true" />
                Benefits of an ESG Training Course
              </h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {benefits.map((benefit, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow bg-gradient-to-br from-emerald-50 to-teal-50">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-emerald-100 p-3 rounded-lg flex-shrink-0">
                          <span className="text-emerald-600 font-bold text-lg" aria-hidden="true">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="text-lg font-bold mb-2 text-gray-900">{benefit.title}</h5>
                          <p className="text-gray-700 text-sm">{benefit.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>

          {/* BRSR India Compliance Section */}
          <section aria-labelledby="brsr-heading" className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h4 id="brsr-heading" className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <BarChart3 className="h-7 w-7 text-emerald-600" aria-hidden="true" />
                BRSR Compliance Training for Indian Corporates
              </h4>
              <Card className="border-l-4 border-emerald-600">
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">
                    The <strong>Business Responsibility and Sustainability Reporting (BRSR)</strong> framework, mandated by <strong>SEBI for the top 1000 listed companies</strong> by market capitalisation, is India's most significant ESG reporting requirement. Our ESG Training Course includes a dedicated BRSR module covering:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    {[
                      'BRSR Core framework — Mandatory vs. Leadership indicators',
                      'SEBI BRSR reporting timelines and compliance obligations',
                      'Mapping BRSR disclosures to GRI, SASB, and TCFD standards',
                      'ESG data collection, verification, and assurance practices',
                      'Supply chain sustainability and value chain disclosures',
                      'Stakeholder engagement and materiality assessment for BRSR'
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                    <p className="text-emerald-800 font-semibold text-sm">
                      Mandatory for: Top 1000 NSE/BSE listed companies (SEBI mandate) &nbsp;|&nbsp; Relevant for: All Indian corporates seeking ESG investor confidence and voluntary reporting
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Choosing Provider - H4 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h4 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Award className="h-7 w-7 text-emerald-600" aria-hidden="true" />
                Choosing the Right ESG Training Course Provider
              </h4>
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">When selecting an ESG training provider, consider:</p>
                  <ul className="grid md:grid-cols-2 gap-4 list-none">
                    {providerCriteria.map((criteria, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{criteria}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 mt-6 font-semibold">
                    A reputable provider ensures the training delivers real business value.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>
        </article>

        {/* Related Courses */}
        <RelatedCoursesSection currentSlug="iso-14001" slugs={['iso-14001', 'iso-50001', 'iso-45001']} heading="Related ISO Training Courses" />

        {/* CTA Section */}
        <aside>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
              <CardContent className="p-12 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Build Your ESG Capabilities?
                </h2>
                <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto">
                  Join sustainability and governance professionals who have transformed their organizations with our comprehensive ESG training programs.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    onClick={() => navigate('/training-calendar')}
                    className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-6 text-lg"
                    size="lg"
                  >
                    <Calendar className="h-5 w-5 mr-2" aria-hidden="true" />
                    View Training Schedule
                  </Button>
                  <Button
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-6 text-lg"
                    size="lg"
                  >
                    Request More Information
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </aside>
      </main>
    </div>
  );
};
